/**
 * Solutions page — editorial presentation layer (FR/EN).
 * Catalog truth remains pep.product.json → portfolio-policy → API.
 * Do not invent availability, clients, KPIs, or product UI evidence.
 */
import type { PepLocale } from "../i18n/types";

export type SolutionsTier = "flagship" | "standard" | "limited";

export type SolutionsNeedId =
  | "education"
  | "hr"
  | "finance"
  | "operations"
  | "industry";

export type SolutionsProductSlug =
  | "edu"
  | "rh"
  | "mineportal"
  | "inventory"
  | "erp"
  | "billing"
  | "hospitol"
  | "security";

export interface SolutionsNeed {
  id: SolutionsNeedId;
  /** Journey depth route (existing /solutions/:slug) */
  journeyHref: string;
  /** Primary product for recognition */
  primaryProduct: SolutionsProductSlug;
  imageSrc: string;
  icon: SolutionsNeedId;
  titleFr: string;
  titleEn: string;
  bodyFr: string;
  bodyEn: string;
  imageAltFr: string;
  imageAltEn: string;
  heroLabelFr: string;
  heroLabelEn: string;
  heroCaptionFr: string;
  heroCaptionEn: string;
}

export interface SolutionsProductEditorial {
  slug: SolutionsProductSlug;
  tier: SolutionsTier;
  /** Commercial order within page sections */
  order: number;
  /** Public commercial name — never the catalog slug */
  commercialNameFr: string;
  commercialNameEn: string;
  imageSrc: string;
  imageAltFr: string;
  imageAltEn: string;
  /** Fallback promise if catalog tagline missing */
  promiseFr: string;
  promiseEn: string;
  /** Fallback capacities (honest, aligned to published scope) */
  capacitiesFr: readonly [string, string, string];
  capacitiesEn: readonly [string, string, string];
  tone: "edu" | "rh" | "mine" | "inventory" | "erp" | "billing" | "hospitol" | "security";
}

/** Need → recognition entries (Owner brief) */
export const SOLUTIONS_NEEDS: readonly SolutionsNeed[] = [
  {
    id: "education",
    journeyHref: "/solutions/vertical-education",
    primaryProduct: "edu",
    imageSrc: "/assets/home/home-solution-edu.webp",
    icon: "education",
    titleFr: "Gérer une institution éducative",
    titleEn: "Run an educational institution",
    bodyFr: "Simplifiez la gestion scolaire, les évaluations et la communication.",
    bodyEn: "Simplify school administration, assessments and communication.",
    imageAltFr: "Illustration éditoriale : environnement scolaire. Ce n’est pas une capture produit.",
    imageAltEn: "Editorial illustration: school environment. Not a product screenshot.",
    heroLabelFr: "Éducation",
    heroLabelEn: "Education",
    heroCaptionFr: "Écoles, collèges et instituts",
    heroCaptionEn: "Schools, colleges and institutes",
  },
  {
    id: "hr",
    journeyHref: "/solutions/human-capital",
    primaryProduct: "rh",
    imageSrc: "/assets/home/home-solution-rh.webp",
    icon: "hr",
    titleFr: "Gérer mes ressources humaines",
    titleEn: "Manage my human resources",
    bodyFr: "Recrutez, administrez, motivez et développez vos talents.",
    bodyEn: "Recruit, administer, motivate and develop your talent.",
    imageAltFr: "Illustration éditoriale : professionnels RH. Ce n’est pas une capture produit.",
    imageAltEn: "Editorial illustration: HR professionals. Not a product screenshot.",
    heroLabelFr: "Capital humain",
    heroLabelEn: "Human capital",
    heroCaptionFr: "Vos équipes, votre plus grande valeur",
    heroCaptionEn: "Your teams, your greatest asset",
  },
  {
    id: "finance",
    journeyHref: "/solutions/finance-operations",
    primaryProduct: "billing",
    imageSrc: "/assets/home/home-solution-billing.webp",
    icon: "finance",
    titleFr: "Mieux piloter mes finances",
    titleEn: "Steer my finances better",
    bodyFr: "Comptabilité, facturation, trésorerie et reporting.",
    bodyEn: "Accounting, billing, cash flow and reporting.",
    imageAltFr: "Illustration éditoriale : pilotage financier. Ce n’est pas une capture produit.",
    imageAltEn: "Editorial illustration: financial steering. Not a product screenshot.",
    heroLabelFr: "Finance & gestion",
    heroLabelEn: "Finance & management",
    heroCaptionFr: "Maîtrisez vos finances, pilotez la croissance",
    heroCaptionEn: "Control finance, steer growth",
  },
  {
    id: "operations",
    journeyHref: "/solutions/supply-chain",
    primaryProduct: "inventory",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    icon: "operations",
    titleFr: "Optimiser mes opérations",
    titleEn: "Optimize my operations",
    bodyFr: "Stocks, achats, ventes, logistique : tout sous contrôle.",
    bodyEn: "Stock, purchasing, sales and logistics under control.",
    imageAltFr: "Illustration éditoriale : entrepôt et opérations. Ce n’est pas une capture produit.",
    imageAltEn: "Editorial illustration: warehouse and operations. Not a product screenshot.",
    heroLabelFr: "Opérations & stocks",
    heroLabelEn: "Operations & inventory",
    heroCaptionFr: "Des stocks maîtrisés, des opérations fluides",
    heroCaptionEn: "Controlled stock, fluid operations",
  },
  {
    id: "industry",
    journeyHref: "/solutions/vertical-mining",
    primaryProduct: "mineportal",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    icon: "industry",
    titleFr: "Sécuriser et développer mon industrie",
    titleEn: "Secure and grow my industrial operations",
    bodyFr: "Production, maintenance, conformité, sécurité et opérations.",
    bodyEn: "Production, maintenance, compliance, safety and operations.",
    imageAltFr: "Illustration éditoriale : contexte industriel et minier. Ce n’est pas une capture produit.",
    imageAltEn: "Editorial illustration: industrial and mining context. Not a product screenshot.",
    heroLabelFr: "Industrie & mines",
    heroLabelEn: "Industry & mining",
    heroCaptionFr: "Solutions robustes pour environnements exigeants",
    heroCaptionEn: "Robust solutions for demanding environments",
  },
] as const;

/**
 * Commercial hierarchy for /solutions (editorial).
 * Flagship ≠ standard ≠ limited — visual weight must differ.
 */
export const SOLUTIONS_PRODUCTS: readonly SolutionsProductEditorial[] = [
  {
    slug: "edu",
    tier: "flagship",
    order: 1,
    commercialNameFr: "ITaaS Edu",
    commercialNameEn: "ITaaS Edu",
    imageSrc: "/assets/home/home-solution-edu.webp",
    imageAltFr: "Illustration éditoriale Éducation — pas une capture produit.",
    imageAltEn: "Education editorial illustration — not a product screenshot.",
    promiseFr: "Une école mieux organisée, des élèves mieux suivis.",
    promiseEn: "A better-organized school, students better followed.",
    capacitiesFr: ["Gestion des élèves et classes", "Évaluations et bulletins", "Communication école–parents"],
    capacitiesEn: ["Student and class management", "Assessments and report cards", "School–parent communication"],
    tone: "edu",
  },
  {
    slug: "rh",
    tier: "flagship",
    order: 2,
    commercialNameFr: "ITaaS RH",
    commercialNameEn: "ITaaS RH",
    imageSrc: "/assets/home/home-solution-rh.webp",
    imageAltFr: "Illustration éditoriale RH — pas une capture produit.",
    imageAltEn: "HR editorial illustration — not a product screenshot.",
    promiseFr: "Vos équipes, votre plus grande valeur.",
    promiseEn: "Your people, your greatest asset.",
    capacitiesFr: ["Dossier collaborateur", "Administration RH", "Portail carrières"],
    capacitiesEn: ["Employee records", "HR administration", "Careers portal"],
    tone: "rh",
  },
  {
    slug: "mineportal",
    tier: "flagship",
    order: 3,
    commercialNameFr: "ITaaS MinePortal",
    commercialNameEn: "ITaaS MinePortal",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    imageAltFr: "Illustration éditoriale mines — pas une capture produit.",
    imageAltEn: "Mining editorial illustration — not a product screenshot.",
    promiseFr: "Sites, sous-traitance et magasins mieux pilotés.",
    promiseEn: "Sites, subcontracting and stores under better control.",
    capacitiesFr: ["Sites et opérations", "Sous-traitance ARSP", "Magasins et traçabilité"],
    capacitiesEn: ["Sites and operations", "ARSP subcontracting", "Stores and traceability"],
    tone: "mine",
  },
  {
    slug: "inventory",
    tier: "standard",
    order: 4,
    commercialNameFr: "ITaaS Inventory",
    commercialNameEn: "ITaaS Inventory",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    imageAltFr: "Illustration éditoriale stocks — pas une capture produit.",
    imageAltEn: "Inventory editorial illustration — not a product screenshot.",
    promiseFr: "Gérez vos stocks et mouvements avec clarté.",
    promiseEn: "Manage stock and movements with clarity.",
    capacitiesFr: ["Stocks multi-entrepôts", "Mouvements et alertes", "Traçabilité"],
    capacitiesEn: ["Multi-warehouse stock", "Movements and alerts", "Traceability"],
    tone: "inventory",
  },
  {
    slug: "erp",
    tier: "standard",
    order: 5,
    commercialNameFr: "ITaaS ERP",
    commercialNameEn: "ITaaS ERP",
    imageSrc: "/assets/home/home-solution-erp.webp",
    imageAltFr: "Illustration éditoriale ERP — pas une capture produit.",
    imageAltEn: "ERP editorial illustration — not a product screenshot.",
    promiseFr: "Organisation et sites sur un socle progressif.",
    promiseEn: "Organization and sites on a progressive foundation.",
    capacitiesFr: ["Organisation et rôles", "Sites et collaboration", "Fondation évolutive"],
    capacitiesEn: ["Organization and roles", "Sites and collaboration", "Evolving foundation"],
    tone: "erp",
  },
  {
    slug: "billing",
    tier: "standard",
    order: 6,
    commercialNameFr: "ITaaS Invoice",
    commercialNameEn: "ITaaS Invoice",
    imageSrc: "/assets/home/home-solution-billing.webp",
    imageAltFr: "Illustration éditoriale facturation — pas une capture produit.",
    imageAltEn: "Billing editorial illustration — not a product screenshot.",
    promiseFr: "Devis, factures et encaissements dans un flux clair.",
    promiseEn: "Quotes, invoices and collections in a clear flow.",
    capacitiesFr: ["Devis et factures", "Suivi des paiements", "Clients centralisés"],
    capacitiesEn: ["Quotes and invoices", "Payment follow-up", "Centralized customers"],
    tone: "billing",
  },
  {
    slug: "hospitol",
    tier: "limited",
    order: 7,
    commercialNameFr: "ITaaS Hospitol",
    commercialNameEn: "ITaaS Hospitol",
    imageSrc: "/assets/home/home-solution-hospitol.webp",
    imageAltFr: "Illustration éditoriale santé — pas une capture produit.",
    imageAltEn: "Healthcare editorial illustration — not a product screenshot.",
    promiseFr: "Pharmacie hospitalière et stock médical — disponibilité limitée.",
    promiseEn: "Hospital pharmacy and medical stock — limited availability.",
    capacitiesFr: ["Pharmacie hospitalière", "Stock médical", "Facturation associée"],
    capacitiesEn: ["Hospital pharmacy", "Medical stock", "Linked billing"],
    tone: "hospitol",
  },
  {
    slug: "security",
    tier: "limited",
    order: 8,
    commercialNameFr: "ITaaS Security",
    commercialNameEn: "ITaaS Security",
    imageSrc: "/assets/home/home-solution-security.webp",
    imageAltFr: "Illustration éditoriale sécurité — pas une capture produit.",
    imageAltEn: "Security editorial illustration — not a product screenshot.",
    promiseFr: "Opérations de sécurité privée — disponibilité limitée.",
    promiseEn: "Private security operations — limited availability.",
    capacitiesFr: ["Clients et sites", "Agents et certifications", "Incidents"],
    capacitiesEn: ["Clients and sites", "Agents and certifications", "Incidents"],
    tone: "security",
  },
] as const;

/** Products that may appear as related links on public journey pages. */
export const SOLUTIONS_PUBLISHABLE_PRODUCT_SLUGS = new Set<string>(
  SOLUTIONS_PRODUCTS.map((p) => p.slug),
);

export interface SolutionsJourneyEditorial {
  slug: string;
  titleFr: string;
  titleEn: string;
  blurbFr: string;
  blurbEn: string;
  longFr: string;
  longEn: string;
  benefitsFr: readonly string[];
  benefitsEn: readonly string[];
  seoTitleFr: string;
  seoTitleEn: string;
  seoDescriptionFr: string;
  seoDescriptionEn: string;
  seoKeywordsFr: readonly string[];
  seoKeywordsEn: readonly string[];
  productSlugs: readonly SolutionsProductSlug[];
}

/** Commercial FR/EN journey layer — depth/SEO only; never promotes HIDDEN modules. */
export const SOLUTIONS_JOURNEYS: readonly SolutionsJourneyEditorial[] = [
  {
    slug: "vertical-education",
    titleFr: "Éducation",
    titleEn: "Education",
    blurbFr: "Gestion scolaire, évaluations et communication parents avec ITaaS Edu.",
    blurbEn: "School administration, assessments and parent communication with ITaaS Edu.",
    longFr:
      "Parcours éducation centré sur ITaaS Edu : élèves, classes, évaluations, bulletins et frais. Composition possible avec RH, stocks et facturation. Bulletins adaptés au contexte scolaire RDC.",
    longEn:
      "Education journey centered on ITaaS Edu: students, classes, assessments, report cards and fees. Optional composition with HR, inventory and billing. Report cards adapted to the DRC school context.",
    benefitsFr: [
      "Gestion des élèves et classes",
      "Évaluations et bulletins",
      "Communication école–parents",
      "Frais et facturation associés",
    ],
    benefitsEn: [
      "Student and class management",
      "Assessments and report cards",
      "School–parent communication",
      "Fees and linked billing",
    ],
    seoTitleFr: "Logiciel de gestion scolaire en RDC",
    seoTitleEn: "School management software in the DRC",
    seoDescriptionFr:
      "Parcours éducation ITaaS : gestion scolaire, évaluations, bulletins et communication parents pour établissements en RDC.",
    seoDescriptionEn:
      "ITaaS education journey: school administration, assessments, report cards and parent communication for institutions in the DRC.",
    seoKeywordsFr: ["logiciel gestion scolaire RDC", "EMS Congo", "bulletins école Afrique"],
    seoKeywordsEn: ["school management software DRC", "EMS Congo", "school report cards Africa"],
    productSlugs: ["edu", "rh", "inventory", "billing"],
  },
  {
    slug: "human-capital",
    titleFr: "Capital humain",
    titleEn: "Human capital",
    blurbFr: "Recrutez, administrez et développez vos équipes avec ITaaS RH.",
    blurbEn: "Recruit, administer and develop your teams with ITaaS RH.",
    longFr:
      "Parcours capital humain centré sur ITaaS RH : dossiers collaborateurs, administration RH et portail carrières. API paie documentée — sans promesse de conformité légale absolue.",
    longEn:
      "Human-capital journey centered on ITaaS RH: employee records, HR administration and careers portal. Documented payroll API — no absolute legal-compliance claim.",
    benefitsFr: [
      "Dossier collaborateur centralisé",
      "Portail carrières",
      "API paie (périmètre documenté)",
      "Composition facturation possible",
    ],
    benefitsEn: [
      "Centralized employee records",
      "Careers portal",
      "Payroll API (documented scope)",
      "Optional billing composition",
    ],
    seoTitleFr: "Gestion des ressources humaines en RDC",
    seoTitleEn: "Human resources management in the DRC",
    seoDescriptionFr:
      "Parcours RH ITaaS : dossiers collaborateurs, administration et recrutement pour organisations en RDC.",
    seoDescriptionEn:
      "ITaaS HR journey: employee records, administration and recruitment for organizations in the DRC.",
    seoKeywordsFr: ["solution RH entreprise RDC", "SIRH Congo", "gestion ressources humaines"],
    seoKeywordsEn: ["HR software DRC", "HRIS Congo", "human resources management"],
    productSlugs: ["rh", "billing"],
  },
  {
    slug: "finance-operations",
    titleFr: "Finance & opérations",
    titleEn: "Finance & operations",
    blurbFr: "Facturation, organisation et pilotage financier pour PME en RDC.",
    blurbEn: "Billing, organization and financial steering for SMEs in the DRC.",
    longFr:
      "Parcours finance autour d’ITaaS Invoice et du socle ITaaS ERP : devis, factures, encaissements et organisation. Périmètre progressif — pas un ERP comptable complet.",
    longEn:
      "Finance journey around ITaaS Invoice and the ITaaS ERP foundation: quotes, invoices, collections and organization. Progressive scope — not a full accounting ERP.",
    benefitsFr: [
      "Devis, factures et paiements",
      "Organisation sur le socle ERP",
      "Lien stocks Inventory",
      "Démo et devis",
    ],
    benefitsEn: [
      "Quotes, invoices and payments",
      "Organization on the ERP foundation",
      "Link to Inventory stock",
      "Demo and quote",
    ],
    seoTitleFr: "Gestion financière et facturation en RDC",
    seoTitleEn: "Financial management and billing in the DRC",
    seoDescriptionFr:
      "Parcours finance ITaaS : facturation, organisation et opérations pour entreprises en RDC.",
    seoDescriptionEn:
      "ITaaS finance journey: billing, organization and operations for businesses in the DRC.",
    seoKeywordsFr: ["logiciel facturation RDC", "gestion financière Congo", "ERP PME"],
    seoKeywordsEn: ["billing software DRC", "financial management Congo", "SME ERP"],
    productSlugs: ["billing", "erp", "inventory"],
  },
  {
    slug: "supply-chain",
    titleFr: "Stocks & opérations",
    titleEn: "Stock & operations",
    blurbFr: "Stocks multi-entrepôts, mouvements et traçabilité avec ITaaS Inventory.",
    blurbEn: "Multi-warehouse stock, movements and traceability with ITaaS Inventory.",
    longFr:
      "Parcours stocks centré sur ITaaS Inventory : entrepôts, mouvements et alertes. Sans promesse d’un WMS ou d’une supply chain bout-en-bout complète.",
    longEn:
      "Stock journey centered on ITaaS Inventory: warehouses, movements and alerts. No claim of a full WMS or end-to-end supply chain.",
    benefitsFr: ["Stocks multi-entrepôts", "Mouvements et alertes", "Traçabilité", "Composition ERP"],
    benefitsEn: ["Multi-warehouse stock", "Movements and alerts", "Traceability", "ERP composition"],
    seoTitleFr: "Gestion des stocks et opérations en RDC",
    seoTitleEn: "Inventory and operations management in the DRC",
    seoDescriptionFr:
      "Parcours stocks ITaaS : multi-entrepôts, mouvements et traçabilité pour opérations en RDC.",
    seoDescriptionEn:
      "ITaaS inventory journey: multi-warehouse stock, movements and traceability for operations in the DRC.",
    seoKeywordsFr: ["gestion de stocks RDC", "logiciel inventaire Congo", "opérations"],
    seoKeywordsEn: ["inventory software DRC", "stock management Congo", "operations"],
    productSlugs: ["inventory", "erp"],
  },
  {
    slug: "vertical-mining",
    titleFr: "Mines & ressources",
    titleEn: "Mining & resources",
    blurbFr: "Portail compagnie minière — sites, sous-traitance et magasins.",
    blurbEn: "Mining company portal — sites, subcontracting and stores.",
    longFr:
      "Parcours mines autour d’ITaaS MinePortal : sites, sous-traitance ARSP et magasins ISRM, avec Inventory, RH et facturation. Modules transit non publiés exclus.",
    longEn:
      "Mining journey around ITaaS MinePortal: sites, ARSP subcontracting and ISRM stores, with Inventory, HR and billing. Unpublished transit modules excluded.",
    benefitsFr: [
      "Sites et opérations",
      "Sous-traitance ARSP",
      "Magasins et traçabilité",
      "Composition RH / stocks / facturation",
    ],
    benefitsEn: [
      "Sites and operations",
      "ARSP subcontracting",
      "Stores and traceability",
      "HR / stock / billing composition",
    ],
    seoTitleFr: "Solutions logicielles pour entreprises minières en RDC",
    seoTitleEn: "Software solutions for mining companies in the DRC",
    seoDescriptionFr:
      "Parcours mines ITaaS : portail sites, sous-traitance et magasins pour opérateurs en RDC.",
    seoDescriptionEn:
      "ITaaS mining journey: site portal, subcontracting and stores for operators in the DRC.",
    seoKeywordsFr: ["logiciel mine RDC", "portail minier Congo", "ARSP Katanga"],
    seoKeywordsEn: ["mining software DRC", "mining portal Congo", "ARSP Katanga"],
    productSlugs: ["mineportal", "rh", "inventory", "billing"],
  },
  {
    slug: "vertical-healthcare",
    titleFr: "Santé & hôpitaux",
    titleEn: "Healthcare & hospitals",
    blurbFr: "Pharmacie hospitalière et opérations santé — disponibilité limitée.",
    blurbEn: "Hospital pharmacy and healthcare operations — limited availability.",
    longFr:
      "Parcours santé autour d’ITaaS Hospitol (disponibilité limitée) : pharmacie hospitalière et stock médical. Pas un HIS complet en disponibilité générale.",
    longEn:
      "Healthcare journey around ITaaS Hospitol (limited availability): hospital pharmacy and medical stock. Not a full HIS in general availability.",
    benefitsFr: [
      "Pharmacie hospitalière",
      "Stock médical",
      "Facturation associée",
      "Démo sur demande",
    ],
    benefitsEn: [
      "Hospital pharmacy",
      "Medical stock",
      "Linked billing",
      "Demo on request",
    ],
    seoTitleFr: "Pharmacie hospitalière et santé en RDC",
    seoTitleEn: "Hospital pharmacy and healthcare in the DRC",
    seoDescriptionFr:
      "Parcours santé ITaaS : pharmacie hospitalière et stock médical — disponibilité limitée en RDC.",
    seoDescriptionEn:
      "ITaaS healthcare journey: hospital pharmacy and medical stock — limited availability in the DRC.",
    seoKeywordsFr: ["pharmacie hospitalière RDC", "logiciel clinique Congo"],
    seoKeywordsEn: ["hospital pharmacy DRC", "clinic software Congo"],
    productSlugs: ["hospitol", "rh", "inventory", "billing"],
  },
  {
    slug: "security-operations",
    titleFr: "Sécurité privée",
    titleEn: "Private security",
    blurbFr: "Opérations de sécurité privée — disponibilité limitée.",
    blurbEn: "Private security operations — limited availability.",
    longFr:
      "Parcours sécurité autour d’ITaaS Security (disponibilité limitée) : clients, sites, agents et incidents.",
    longEn:
      "Security journey around ITaaS Security (limited availability): clients, sites, agents and incidents.",
    benefitsFr: ["Clients et sites", "Agents et certifications", "Incidents", "Composition RH"],
    benefitsEn: ["Clients and sites", "Agents and certifications", "Incidents", "HR composition"],
    seoTitleFr: "Gestion de la sécurité privée en RDC",
    seoTitleEn: "Private security management in the DRC",
    seoDescriptionFr:
      "Parcours sécurité ITaaS : clients, sites, agents et incidents — disponibilité limitée.",
    seoDescriptionEn:
      "ITaaS security journey: clients, sites, agents and incidents — limited availability.",
    seoKeywordsFr: ["logiciel gardiennage RDC", "sécurité privée Congo"],
    seoKeywordsEn: ["security software DRC", "private security Congo"],
    productSlugs: ["security", "rh", "inventory"],
  },
  {
    slug: "vertical-logistics",
    titleFr: "Transit & réception site",
    titleEn: "Inbound & site receiving",
    blurbFr: "Réception site et stocks — liés à Inventory et MinePortal.",
    blurbEn: "Site receiving and stock — linked to Inventory and MinePortal.",
    longFr:
      "Parcours logistique orienté réception et stocks sur site via Inventory et MinePortal. Un module transit/douane dédié n’est pas publié au catalogue public.",
    longEn:
      "Logistics journey focused on site receiving and stock via Inventory and MinePortal. A dedicated customs/transit module is not published in the public catalog.",
    benefitsFr: [
      "Stocks et réceptions site",
      "Lien opérations minières",
      "Facturation associée",
      "Échange expert sur le besoin transit",
    ],
    benefitsEn: [
      "Site stock and receiving",
      "Link to mining operations",
      "Linked billing",
      "Expert discussion for transit needs",
    ],
    seoTitleFr: "Stocks et réception site en RDC",
    seoTitleEn: "Stock and site receiving in the DRC",
    seoDescriptionFr:
      "Parcours logistique ITaaS : réception site et stocks pour flux opérationnels en RDC.",
    seoDescriptionEn:
      "ITaaS logistics journey: site receiving and stock for operational flows in the DRC.",
    seoKeywordsFr: ["gestion stocks logistique RDC", "réception site Congo"],
    seoKeywordsEn: ["logistics inventory DRC", "site receiving Congo"],
    productSlugs: ["inventory", "mineportal", "billing"],
  },
  {
    slug: "commercial-growth",
    titleFr: "Croissance commerciale",
    titleEn: "Commercial growth",
    blurbFr: "Devis, factures et encaissements pour équipes commerciales.",
    blurbEn: "Quotes, invoices and collections for commercial teams.",
    longFr:
      "Parcours commercial centré sur ITaaS Invoice : clients, devis, factures et paiements. Un module de pipeline commercial autonome n’est pas présenté comme produit catalogue public.",
    longEn:
      "Commercial journey centered on ITaaS Invoice: customers, quotes, invoices and payments. A standalone commercial pipeline module is not presented as a public catalog product.",
    benefitsFr: ["Devis et factures", "Suivi des encaissements", "Clients centralisés", "Démo"],
    benefitsEn: ["Quotes and invoices", "Collection follow-up", "Centralized customers", "Demo"],
    seoTitleFr: "Facturation et croissance commerciale en RDC",
    seoTitleEn: "Billing and commercial growth in the DRC",
    seoDescriptionFr:
      "Parcours commercial ITaaS : devis, factures et encaissements pour entreprises en RDC.",
    seoDescriptionEn:
      "ITaaS commercial journey: quotes, invoices and collections for businesses in the DRC.",
    seoKeywordsFr: ["logiciel facturation PME RDC", "devis factures Congo"],
    seoKeywordsEn: ["SME billing software DRC", "quotes invoices Congo"],
    productSlugs: ["billing"],
  },
] as const;

export function getJourneyEditorial(slug: string): SolutionsJourneyEditorial | undefined {
  return SOLUTIONS_JOURNEYS.find((j) => j.slug === slug);
}

export function solutionsCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en
      ? "Business software solutions in the DRC | ITaaS Suite"
      : "Solutions logicielles pour entreprises en RDC | ITaaS Suite",
    seoDescription: en
      ? "Discover ITaaS Suite business solutions for organizations in the DRC — education, HR, billing, inventory, mining and more. Match your challenge to the right product."
      : "Découvrez les solutions logicielles ITaaS Suite pour organisations en RDC — éducation, RH, facturation, stocks, mines et plus. Associez votre défi au bon produit.",
    seoKeywords: en
      ? [
          "business software DRC",
          "management software Congo",
          "school management software",
          "HR software DRC",
          "inventory software",
          "mining software DRC",
          "ITaaS Suite",
        ]
      : [
          "solutions logicielles RDC",
          "logiciel de gestion Congo",
          "logiciel gestion scolaire",
          "gestion ressources humaines RDC",
          "gestion des stocks",
          "solutions minières RDC",
          "ITaaS Suite",
        ],
    heroH1Before: en ? "Concrete solutions for your " : "Des solutions concrètes pour vos ",
    heroH1Emphasis: en ? "business challenges." : "défis métiers.",
    heroLead: en
      ? "ITaaS Suite brings together integrated business solutions that simplify operations, strengthen performance and accelerate growth."
      : "ITaaS Suite regroupe des solutions métiers intégrées qui simplifient vos opérations, renforcent la performance et accélèrent votre croissance.",
    primaryCta: en ? "Create my environment" : "Créer mon environnement",
    secondaryCta: en ? "Browse the product catalog" : "Voir le catalogue produits",
    primaryHref: "/configure",
    secondaryHref: "/products",
    /** Honest trust chips — only claims backed by institutional / product truth */
    trust: en
      ? ([
          { id: "rdc", label: "Built in the DRC" },
          { id: "lubum", label: "Publisher in Lubumbashi" },
          { id: "secure", label: "Secured data" },
        ] as const)
      : ([
          { id: "rdc", label: "Développé en RDC" },
          { id: "lubum", label: "Éditeur à Lubumbashi" },
          { id: "secure", label: "Données sécurisées" },
        ] as const),
    needsTitle: en
      ? "Which challenge do you want to solve today?"
      : "Quel défi souhaitez-vous résoudre aujourd’hui ?",
    needsTitleEmphasis: en ? "challenge" : "défi",
    needsLead: en
      ? "Select your priority to discover recommended solutions."
      : "Sélectionnez votre priorité pour découvrir les solutions recommandées.",
    needsAllJourneys: en ? "See all business journeys" : "Voir tous les parcours métiers",
    needsExplore: en ? "Explore" : "Explorer",
    flagshipTitle: en ? "Our flagship solutions" : "Nos solutions phares",
    flagshipAllProducts: en ? "Browse the product catalog" : "Voir le catalogue produits",
    flagshipDiscover: en ? "Discover" : "Découvrir",
    standardEyebrow: en ? "Also in the Suite" : "Aussi dans la Suite",
    badgeFlagship: en ? "FLAGSHIP" : "PHARE",
    badgeStandard: en ? "STANDARD" : "STANDARD",
    badgeLimited: en ? "LIMITED" : "LIMITÉ",
    limitedTitle: en ? "Limited availability" : "Disponibilité limitée",
    limitedBadge: en ? "Limited availability" : "Disponibilité limitée",
    limitedLead: en
      ? "These solutions are available in a controlled scope — not presented as generally available flagships."
      : "Ces solutions sont disponibles dans un périmètre contrôlé — elles ne sont pas présentées comme des phares en disponibilité générale.",
    platformTitle: en
      ? "All our solutions share a common platform"
      : "Toutes nos solutions partagent une plateforme commune",
    platformLead: en
      ? "Several business solutions, one shared technology foundation — identity, data, workflows and reporting on ITaaS Platform."
      : "Plusieurs solutions métier, une fondation technologique commune — identité, données, workflows et reporting sur ITaaS Platform.",
    platformCta: en ? "See why ITaaS" : "Voir pourquoi ITaaS",
    platformHref: "/why-itaas",
    platformCenter: "ITaaS PLATFORM",
    platformNodes: en
      ? (["Identity", "Data", "Workflows", "Reporting"] as const)
      : (["Identité", "Données", "Workflows", "Reporting"] as const),
    finalTitle: en
      ? "Ready to turn your challenges into performance?"
      : "Prêt à transformer vos défis en performance ?",
    finalLead: en
      ? "ITaaS experts help you identify the solution that fits your organization."
      : "Nos experts vous accompagnent pour identifier la solution la plus adaptée à votre organisation.",
    finalDemo: en ? "Request a demonstration" : "Demander une démonstration",
    finalContact: en ? "Contact us" : "Nous contacter",
    journeysAnchorLabel: en ? "Business journeys" : "Parcours métiers",
    carouselPrev: en ? "Previous" : "Précédent",
    carouselNext: en ? "Next" : "Suivant",
    loading: en ? "Loading…" : "Chargement…",
    detailBenefits: en ? "Key benefits" : "Bénéfices clés",
    detailProducts: en ? "Related products" : "Produits inclus",
    breadcrumbSolutions: "Solutions",
  };
}

export function getSolutionsByTier(tier: SolutionsTier) {
  return SOLUTIONS_PRODUCTS.filter((p) => p.tier === tier).sort((a, b) => a.order - b.order);
}
